import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { Module } from 'dashboard/lib/module';
import AccountInfo from 'dashboard/modules/AccountInfo/AccountInfo';
import DataDisplay from 'dashboard/modules/DataDisplay';
import EventTimeline from 'dashboard/modules/EventTimeline';
import GraphV2 from 'dashboard/modules/GraphV2';
import ListMatches from 'dashboard/modules/ListMatches';
import Markdown from 'dashboard/modules/Markdown';
import RelatedCases from 'dashboard/modules/RelatedCases';
import Relations from 'dashboard/modules/Relations';
import ResourceV2 from 'dashboard/modules/Resource/ResourceV2';
import { ModuleResourceInquiryV2 } from 'dashboard/modules/ResourceInquiryV2';
import { Tags } from 'dashboard/modules/Tags/Tags';
import { AccountDataContextProvider } from 'dashboard/routes/DashboardAccountsShowRoute/useAccountDataContext';
import { AccountApi } from 'dashboard/views/DashboardAccountsShowView/DashboardAccountsShowView';

export interface AccountModuleBaseProps<M extends Module> {
  module: M;
  accountApi: AccountApi;
  isPreview?: boolean;
}

export function AccountModuleBase<M extends Module>({
  module,
  accountApi,
  isPreview = false,
}: AccountModuleBaseProps<M>) {
  const {
    account,
    accountVariableDefinitions,
    accountType,
    currentEnvironment,
    relations,
    tags,
    users,
    lists,
    listItems,
    listMatches,
    handleUpdateTags,
  } = accountApi;

  const renderModule = () => {
    switch (module.type) {
      case ModuleTypes.AccountInfo:
        return <AccountInfo account={account} accountType={accountType} module={module} />;
      case ModuleTypes.DataDisplay: {
        return (
          <AccountDataContextProvider
            accountVariableDefinitions={accountVariableDefinitions}
            accountId={account.id}
          >
            {(data) => <DataDisplay module={module} data={data} isPreview={isPreview} />}
          </AccountDataContextProvider>
        );
      }
      case ModuleTypes.Markdown:
        return (
          <AccountDataContextProvider
            accountVariableDefinitions={accountVariableDefinitions}
            accountId={account.id}
          >
            {(data) => <Markdown module={module} templateData={data} isPreview={isPreview} />}
          </AccountDataContextProvider>
        );
      case ModuleTypes.Relations:
        return <Relations relations={relations} module={module} />;
      case ModuleTypes.RelatedCases:
        // TODO: use related cases from accounts data context
        return (
          <RelatedCases
            accounts={[account]}
            currentEnvironment={currentEnvironment}
            users={users}
          />
        );
      case ModuleTypes.Tags: {
        return <Tags tags={tags} handleUpdateTags={handleUpdateTags} />;
      }
      case ModuleTypes.Resource: {
        // TODO: select dependent variable definitions only
        return (
          <AccountDataContextProvider
            accountVariableDefinitions={accountVariableDefinitions}
            accountId={account.id}
          >
            {(data) => (
              <ResourceV2
                module={module as ModuleResourceInquiryV2}
                currentObjectToken={account.id}
                resources={data}
              />
            )}
          </AccountDataContextProvider>
        );
      }
      case ModuleTypes.EventTimeline: {
        return (
          <EventTimeline
            module={module}
            subject={account}
            objects={[]}
            currentEnvironment={currentEnvironment}
            isPreview={isPreview}
          />
        );
      }
      case ModuleTypes.Graphv2: {
        return (
          <GraphV2
            accounts={[account]}
            caseQueryResults={[]}
            attachedGraphQueries={[]}
            handleUpdateFields={() => {}}
            handleRefreshCaseQueryResult={() => {}}
            isPreview={isPreview}
            module={module}
            kase={undefined}
          />
        );
      }
      case ModuleTypes.ListMatches: {
        return (
          <ListMatches
            lists={lists}
            listItems={listItems}
            listMatches={listMatches}
            module={module}
            isLoading={false}
          />
        );
      }
      default:
        return null;
    }
  };
  return <>{renderModule()}</>;
}
